import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SUPPORTED_CURRENCIES } from '../services/currencyService';
import { profileAPI } from '../services/api';

interface UserProfile {
  id: number;
  email: string;
  default_currency: string;
  theme: 'light' | 'dark';
  notifications_enabled: boolean;
  data_export_enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Profile form state
  const [formData, setFormData] = useState({
    default_currency: 'CAD',
    theme: 'light' as 'light' | 'dark',
    notifications_enabled: true,
    data_export_enabled: true
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      // For now, use mock data since the API doesn't exist yet
      const savedTheme = localStorage.getItem('theme') || 'light';
      const mockProfile: UserProfile = {
        id: 1,
        email: 'user@example.com',
        default_currency: 'CAD',
        theme: savedTheme as 'light' | 'dark',
        notifications_enabled: true,
        data_export_enabled: true,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      };
      
      setProfile(mockProfile);
      setFormData({
        default_currency: mockProfile.default_currency,
        theme: mockProfile.theme,
        notifications_enabled: mockProfile.notifications_enabled,
        data_export_enabled: mockProfile.data_export_enabled
      });
      
      // Apply current theme
      applyTheme(mockProfile.theme);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // For now, simulate API call since the backend doesn't exist yet
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setProfile(prev => prev ? { ...prev, ...formData, updated_at: new Date().toISOString() } : null);
      setSuccess('Profile updated successfully!');
      
      // Apply theme change immediately
      applyTheme(formData.theme);
      
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setError(null);
    setSuccess(null);

    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      setPasswordLoading(false);
      return;
    }

    try {
      await profileAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setSuccess('Password changed successfully!');
      setShowPasswordForm(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Failed to change password. Please check your current password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const applyTheme = (theme: 'light' | 'dark') => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('dark-theme', 'light-theme');
    
    // Add the new theme class
    root.classList.add(`${theme}-theme`);
    
    // Also set a data attribute for additional styling if needed
    root.setAttribute('data-theme', theme);
    
    // Save theme preference to localStorage
    localStorage.setItem('theme', theme);
    
    console.log(`Theme applied: ${theme}`);
  };

  const exportData = async () => {
    try {
      setLoading(true);
      const data = await profileAPI.exportData();
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `networth_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      setError('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently lost.')) {
      return;
    }

    if (!window.confirm('This is your final warning. All your financial data, accounts, and history will be permanently deleted. Are you absolutely sure?')) {
      return;
    }

    try {
      setLoading(true);
      await profileAPI.deleteAccount();
      
      // Redirect to logout or show success message
      setSuccess('Account deleted successfully');
      
    } catch (error) {
      console.error('Error deleting account:', error);
      setError('Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container dark-theme">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="profile-header">
        <h1>Profile Settings</h1>
        <p>Manage your account preferences and security settings</p>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          <span className="success-icon">✅</span>
          {success}
        </div>
      )}

      <div className="profile-grid">
        {/* Account Information */}
        <div className="profile-card">
          <div className="card-header">
            <h2>Account Information</h2>
            <div className="card-icon">👤</div>
          </div>
          <div className="account-info">
            <div className="info-item">
              <label>Email</label>
              <span>{profile?.email}</span>
            </div>
            <div className="info-item">
              <label>Member Since</label>
              <span>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Last Updated</label>
              <span>{profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString() : 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="profile-card">
          <div className="card-header">
            <h2>Preferences</h2>
            <div className="card-icon">⚙️</div>
          </div>
          <form onSubmit={handleProfileSave}>
            <div className="form-group">
              <label htmlFor="default_currency">Default Currency</label>
              <select
                id="default_currency"
                value={formData.default_currency}
                onChange={(e) => setFormData({ ...formData, default_currency: e.target.value })}
                className="form-control"
              >
                {SUPPORTED_CURRENCIES.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} ({currency.symbol}) - {currency.name}
                  </option>
                ))}
              </select>
              <small>This currency will be used as the default for displaying all values on your dashboard.</small>
            </div>

            <div className="form-group">
              <label htmlFor="theme">Theme</label>
              <select
                id="theme"
                value={formData.theme}
                onChange={(e) => setFormData({ ...formData, theme: e.target.value as 'light' | 'dark' })}
                className="form-control"
              >
                <option value="light">Light Theme</option>
                <option value="dark">Dark Theme</option>
              </select>
              <small>Choose your preferred visual theme for the application.</small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.notifications_enabled}
                  onChange={(e) => setFormData({ ...formData, notifications_enabled: e.target.checked })}
                />
                <span className="checkmark"></span>
                Enable Notifications
              </label>
              <small>Receive alerts about important financial milestones and updates.</small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.data_export_enabled}
                  onChange={(e) => setFormData({ ...formData, data_export_enabled: e.target.checked })}
                />
                <span className="checkmark"></span>
                Allow Data Export
              </label>
              <small>Enable the ability to export your financial data for backup purposes.</small>
            </div>

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
          </form>
        </div>

        {/* Security */}
        <div className="profile-card">
          <div className="card-header">
            <h2>Security</h2>
            <div className="card-icon">🔒</div>
          </div>
          
          {!showPasswordForm ? (
            <div className="security-actions">
              <button
                onClick={() => setShowPasswordForm(true)}
                className="btn btn-secondary"
              >
                Change Password
              </button>
              <p className="security-note">
                Keep your password strong and unique. Consider using a password manager.
              </p>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="form-control"
                  required
                  minLength={8}
                />
                <small>Password must be at least 8 characters long.</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={passwordLoading}>
                  {passwordLoading ? 'Changing Password...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Data Management */}
        <div className="profile-card">
          <div className="card-header">
            <h2>Data Management</h2>
            <div className="card-icon">📊</div>
          </div>
          
          <div className="data-actions">
            <button
              onClick={exportData}
              className="btn btn-secondary"
              disabled={!formData.data_export_enabled}
            >
              Export My Data
            </button>
            <p className="data-note">
              Download all your financial data as a JSON file for backup or migration purposes.
            </p>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="profile-card danger-zone">
          <div className="card-header">
            <h2>Danger Zone</h2>
            <div className="card-icon">⚠️</div>
          </div>
          
          <div className="danger-actions">
            <button
              onClick={() => {
                localStorage.removeItem('wealthini_onboarding_complete');
                localStorage.removeItem('wealthini_no_business');
                localStorage.removeItem('wealthini_checklist_dismissed');
                localStorage.setItem('wealthini_force_onboarding', 'true');
                navigate('/onboarding');
              }}
              className="btn btn-secondary"
            >
              Restart Onboarding
            </button>
            <p className="danger-note">
              Walk through the onboarding flow again. Your existing data won't be affected.
            </p>
          </div>

          <div className="danger-actions">
            <button
              onClick={deleteAccount}
              className="btn btn-danger"
              disabled={loading}
            >
              Delete Account
            </button>
            <p className="danger-note">
              This action cannot be undone. All your data will be permanently deleted.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 